import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, 
  ChevronLeft, 
  Upload, 
  Eye, 
  CheckCircle, 
  Store,
  Calendar,
  FileText,
  Loader,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StepIndicator from '../components/StepIndicator';
import BasicInfoStep from '../components/wizard/BasicInfoStep';
import FileUploadStep from '../components/wizard/FileUploadStep';
import ProcessingStep from '../components/wizard/ProcessingStep';
import ReviewStep from '../components/wizard/ReviewStep';
import FinalStep from '../components/wizard/FinalStep';

const OffersWizard: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    basicInfo: null,
    files: [],
    processing: null,
    products: [],
    review: null
  });

  const steps = [
    { 
      id: 1, 
      title: 'معلومات أساسية', 
      description: 'اختر التاجر وتفاصيل العرض',
      icon: Store,
      status: currentStep > 1 ? 'completed' : currentStep === 1 ? 'current' : 'upcoming'
    },
    { 
      id: 2, 
      title: 'رفع الملفات', 
      description: 'ارفع صور الفلاير',
      icon: Upload,
      status: currentStep > 2 ? 'completed' : currentStep === 2 ? 'current' : 'upcoming'
    },
    { 
      id: 3, 
      title: 'المعالجة', 
      description: 'كشف واستخراج المنتجات',
      icon: Loader,
      status: currentStep > 3 ? 'completed' : currentStep === 3 ? 'current' : 'upcoming'
    },
    { 
      id: 4, 
      title: 'المراجعة', 
      description: 'راجع وعدل البيانات',
      icon: Eye,
      status: currentStep > 4 ? 'completed' : currentStep === 4 ? 'current' : 'upcoming'
    },
    { 
      id: 5, 
      title: 'النشر', 
      description: 'مراجعة نهائية ونشر',
      icon: CheckCircle,
      status: currentStep > 5 ? 'completed' : currentStep === 5 ? 'current' : 'upcoming'
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepId: number) => {
    if (stepId <= currentStep) {
      setCurrentStep(stepId);
    }
  };

  const handleCancel = () => {
    navigate('/offers');
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInfoStep
            data={formData.basicInfo}
            onUpdate={(data) => setFormData({...formData, basicInfo: data, offer_id: data.offer_id})}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <FileUploadStep
            data={formData.files}
            onUpdate={(files) => setFormData({...formData, files})}
            onNext={handleNext}
            onPrevious={handlePrevious}
            offerId={formData.offer_id}
          />
        );
      case 3:
        return (
          <ProcessingStep
            files={formData.files}
            basicInfo={formData.basicInfo}
            onUpdate={(data) => setFormData({...formData, processing: data})}
            onNext={handleNext}
            onPrevious={handlePrevious}
            offerId={formData.offer_id}
          />
        );
      case 4:
        return (
          <ReviewStep
            products={formData.products}
            onUpdate={(products) => setFormData({...formData, products})}
            onNext={handleNext}
            onPrevious={handlePrevious}
            offerId={formData.offer_id}
          />
        );
      case 5:
        return (
          <FinalStep
            formData={formData}
            onPublish={() => {
              // Handle publish logic
              navigate('/offers');
            }}
            onPrevious={handlePrevious}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4 space-x-reverse">
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                إنشاء عرض جديد
              </h1>
            </div>
            <div className="flex items-center space-x-3 space-x-reverse">
              <button
                onClick={handleCancel}
                className="btn-outline"
              >
                إلغاء
              </button>
              <span className="text-sm text-gray-500">
                الخطوة {currentStep} من {steps.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <StepIndicator
            steps={steps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-xl shadow-soft"
          >
            {renderCurrentStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OffersWizard;